import React, { useEffect, useState } from "react";
import API_BASE from "./config";

// Public page reached from the client invitation email
// (cloudgenomics.net/client-register?token=...). Validates the token, then lets the
// invited contact set a name + password. On success the backend creates their Cognito
// account in the 'client' group and links it to their client_id; they then sign in and
// land in the isolated client portal.
const ClientRegister = () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token") || "";

    const [state, setState] = useState({ status: "validating" }); // validating | invalid | form | submitting | done
    const [invite, setInvite] = useState(null);
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            if (!token) { setState({ status: "invalid" }); return; }
            try {
                const res = await fetch(`${API_BASE}/client-invite/validate?token=${encodeURIComponent(token)}`);
                const data = await res.json();
                if (data.valid) { setInvite(data); setState({ status: "form" }); }
                else setState({ status: "invalid", reason: data.reason });
            } catch (e) { setState({ status: "invalid", reason: "server_error" }); }
        })();
    }, [token]);

    const submit = async () => {
        setError("");
        if (!name.trim()) return setError("Please enter your name.");
        if (password.length < 8) return setError("Password must be at least 8 characters.");
        if (password !== confirm) return setError("Passwords do not match.");
        setState({ status: "submitting" });
        try {
            const res = await fetch(`${API_BASE}/client-invite/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, name: name.trim(), password }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Registration failed.");
            setState({ status: "done" });
        } catch (e) { setError(e.message); setState({ status: "form" }); }
    };

    const card = { maxWidth: 460, margin: "60px auto", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 28, boxShadow: "0 20px 40px rgba(0,0,0,0.08)" };
    const input = { width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: 8, marginBottom: 10, boxSizing: "border-box" };

    return (
        <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: "Arial, sans-serif" }}>
            <div style={card}>
                <h2 style={{ color: "#213a22", marginTop: 0 }}>CloudGenomics Client Registration</h2>

                {state.status === "validating" && <p>Validating your invitation…</p>}

                {state.status === "invalid" && (
                    <p style={{ color: "#7f1d1d" }}>This invitation link is invalid or has expired ({state.reason || "unknown"}). Please contact CloudGenomics for a new invitation.</p>
                )}

                {(state.status === "form" || state.status === "submitting") && (
                    <>
                        <p style={{ color: "#475569" }}>Setting up access for <strong>{invite?.client_name}</strong> ({invite?.invitee_email}).</p>
                        {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#7f1d1d", padding: "8px 12px", borderRadius: 8, marginBottom: 10 }}>{error}</div>}
                        <input style={input} placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)} />
                        <input style={input} type="password" placeholder="Create a password (min 8 chars)" value={password} onChange={(e) => setPassword(e.target.value)} />
                        <input style={input} type="password" placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                        <button onClick={submit} disabled={state.status === "submitting"}
                            style={{ width: "100%", padding: "12px", background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", border: "none", borderRadius: 10, fontWeight: "bold", cursor: "pointer" }}>
                            {state.status === "submitting" ? "Creating your account…" : "Create account"}
                        </button>
                    </>
                )}

                {state.status === "done" && (
                    <>
                        <p style={{ color: "#065f46" }}>✅ Your account has been created.</p>
                        <a href="/login" style={{ display: "inline-block", marginTop: 8, padding: "12px 20px", background: "#213a22", color: "#fff", textDecoration: "none", borderRadius: 10, fontWeight: "bold" }}>Sign in to the client portal</a>
                    </>
                )}
            </div>
        </div>
    );
};

export default ClientRegister;
